import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical, Plus } from 'lucide-react';
import type { OpticalLabSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import {
  useCreateOpticalLabMutation,
  useOpticalLabsListQuery,
  useUpdateOpticalLabMutation,
} from '../api/opticalJobs.api';
import { trackingErrorMessage } from '../tracking/errors';

type Notice = { tone: 'success' | 'error'; text: string };

/**
 * The labs jobs are sent to: add one, rename or reclassify it, deactivate or reactivate it.
 *
 * Labs are never deleted — a past job keeps naming the lab it went to, active or not. An inactive lab cannot be chosen for a new job or a send. One name per business; the server refuses a duplicate. No supplier link, purchasing or integration: those arrive with their own modules.
 */
export function OpticalLabsPage() {
  const { t } = useTranslation();
  const labs = useOpticalLabsListQuery(true);
  const create = useCreateOpticalLabMutation();
  const [name, setName] = useState('');
  const [inHouse, setInHouse] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const nameError =
    name.trim().length === 0
      ? t('opticalOrders.labs.nameRequired')
      : name.trim().length > 120
        ? t('opticalOrders.labs.nameTooLong')
        : null;

  const add = () => {
    setNotice(null);
    if (nameError) {
      setShowErrors(true);
      return;
    }
    create.mutate(
      { name: name.trim(), isInHouse: inHouse },
      {
        onSuccess: (lab) => {
          setName('');
          setInHouse(false);
          setShowErrors(false);
          setNotice({ tone: 'success', text: t('opticalOrders.labs.added', { name: lab.name }) });
        },
        onError: (caught) =>
          setNotice({
            tone: 'error',
            text:
              apiErrorCode(caught) === 'CONFLICT'
                ? t('opticalOrders.labs.duplicate')
                : trackingErrorMessage(t, caught),
          }),
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('opticalOrders.labs.title')}
        subtitle={t('opticalOrders.labs.subtitle')}
      />
      <div aria-live="polite">
        {notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : null}
      </div>

      <Card>
        <CardHeader title={t('opticalOrders.labs.addTitle')} />
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-end">
          <Input
            label={t('opticalOrders.labs.name')}
            required
            maxLength={120}
            dir="auto"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={showErrors && nameError ? nameError : undefined}
          />
          <label className="text-ink flex h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={inHouse}
              onChange={(event) => setInHouse(event.target.checked)}
            />
            {t('opticalOrders.labs.inHouseQuestion')}
          </label>
          <Button onClick={add} isLoading={create.isPending}>
            <Plus className="size-4" aria-hidden />
            {t('opticalOrders.labs.add')}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t('opticalOrders.labs.listTitle')}
          subtitle={t('opticalOrders.labs.listHint')}
        />
        {labs.isError ? (
          <div className="px-5 py-4">
            <Alert tone="error">{trackingErrorMessage(t, labs.error)}</Alert>
          </div>
        ) : labs.isPending ? (
          <LoadingState />
        ) : (labs.data ?? []).length === 0 ? (
          <EmptyState
            icon={FlaskConical}
            title={t('opticalOrders.labs.none')}
            description={t('opticalOrders.labs.noneHint')}
          />
        ) : (
          <ul className="divide-line divide-y">
            {(labs.data ?? []).map((lab) => (
              <LabRow key={lab.id} lab={lab} onNotice={setNotice} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function LabRow({ lab, onNotice }: { lab: OpticalLabSummary; onNotice: (notice: Notice) => void }) {
  const { t } = useTranslation();
  const update = useUpdateOpticalLabMutation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(lab.name);
  const [inHouse, setInHouse] = useState(lab.isInHouse);

  const change = (body: { name?: string; isInHouse?: boolean; isActive?: boolean }, done: string) =>
    update.mutate(
      { id: lab.id, body },
      {
        onSuccess: () => {
          setEditing(false);
          onNotice({ tone: 'success', text: done });
        },
        onError: (caught) =>
          onNotice({
            tone: 'error',
            text:
              apiErrorCode(caught) === 'CONFLICT'
                ? t('opticalOrders.labs.duplicate')
                : trackingErrorMessage(t, caught),
          }),
      },
    );

  return (
    <li className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
      {editing ? (
        <div className="grid flex-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <Input
            label={t('opticalOrders.labs.name')}
            maxLength={120}
            dir="auto"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <label className="text-ink flex h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={inHouse}
              onChange={(event) => setInHouse(event.target.checked)}
            />
            {t('opticalOrders.labs.inHouseQuestion')}
          </label>
        </div>
      ) : (
        <div className="min-w-0 space-y-1">
          <p className="text-ink font-medium break-words" dir="auto">
            {lab.name}
          </p>
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-muted">
              {t(lab.isInHouse ? 'opticalOrders.labs.inHouse' : 'opticalOrders.labs.external')}
            </span>
            <Badge tone={lab.isActive ? 'success' : 'warning'}>
              {t(lab.isActive ? 'opticalOrders.labs.active' : 'opticalOrders.labs.inactive')}
            </Badge>
          </p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={update.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              isLoading={update.isPending}
              disabled={!name.trim()}
              onClick={() =>
                change(
                  { name: name.trim(), isInHouse: inHouse },
                  t('opticalOrders.labs.saved', { name: name.trim() }),
                )
              }
            >
              {t('opticalOrders.labs.save')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              aria-label={t('opticalOrders.labs.editNamed', { name: lab.name })}
              onClick={() => {
                setName(lab.name);
                setInHouse(lab.isInHouse);
                setEditing(true);
              }}
            >
              {t('opticalOrders.labs.edit')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              isLoading={update.isPending}
              aria-label={t(
                lab.isActive
                  ? 'opticalOrders.labs.deactivateNamed'
                  : 'opticalOrders.labs.reactivateNamed',
                { name: lab.name },
              )}
              onClick={() =>
                change(
                  { isActive: !lab.isActive },
                  t(
                    lab.isActive
                      ? 'opticalOrders.labs.deactivated'
                      : 'opticalOrders.labs.reactivated',
                    { name: lab.name },
                  ),
                )
              }
            >
              {t(lab.isActive ? 'opticalOrders.labs.deactivate' : 'opticalOrders.labs.reactivate')}
            </Button>
          </>
        )}
      </div>
    </li>
  );
}
